import { FC, useContext, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import useDataInitializer from "../../hooks/useDataInitializer";
import { useDebounce } from "../../hooks/useDebounce";
import useListFilters from "../../hooks/useListFilters";
import { useI18n } from "../../hooks/useTranslations";
import { useDueDatesApi } from "../../http/dueDates/api";
import { DueDateNoCarDependency, isItemExpired, isItemExpiringSoon } from "../../http/dueDates/types";
import { NestedPick, PaginationQueryWithFields } from "../../types";
import { formatDateNumeric, isWithinLast48h } from "../../types/utils";
import Button from "../ui/Button";
import Header from "../ui/Header";
import Panel from "../ui/Panel";
import Table from "../ui/Table";

import NotificationDispatch, { showInformativeNotification } from "../../context/notificationContext";
import { useUserSettings } from "../../context/userSettingsContext";
import SvgAdd from "../icons/Add";
import SvgAttach from "../icons/Attach";
import SvgCancel from "../icons/Cancel";
import SvgCheckCircle from "../icons/CheckCircle";
import SvgRefresh from "../icons/Refresh";
import { Check } from "../ui/Check";
import CustomLink from "../ui/CustomLink";
import { Action, getDeleteAction, getEditAction, getHistoryAction } from "../ui/ListItemMenu";
import NewItemIcon from "../ui/NewItemIcon";
import BooleanColumnFilter from "../ui/columnFilters/BooleanColumnFilter";
import DateColumnFilter from "../ui/columnFilters/DateColumnFilter";
import TextColumnFilter from "../ui/columnFilters/TextColumnFilter";
import CreateDueDateDialog from "./CreateDueDateDialog";
import DeleteDueDateDialog from "./DeleteDueDateDialog";
import DueDateHistroryDialog from "./DueDateHistroryDialog";
import DueDateStats from "./DueDateStats";
import EditDueDate from "./EditDueDateDialog";
import RenewDueDatesDialog from "./RenewDueDatesDialog";
import SetSettledDueDatesDialog from "./SetSettledDueDatesDialog";
import DueDateTypes from "./types/DueDateTypes";
import EditDueDateTypeDialog from "./types/EditDueDateTypeDialog";

const DUE_DATES_FIELDS = [
  "id",
  "createdAt",
  "car.id",
  "car.vehicleIdentificationNumber",
  "car.currentPlateNumber",
  "car.lastPlateNumber",
  "car.currentAssignment",
  "car.currentAssignment.carDriver.id",
  "car.currentAssignment.carDriver.driver.id",
  "car.currentAssignment.carDriver.driver.fullName",
  "car.currentAssignment.otherCarAssignment.id",
  "car.currentAssignment.otherCarAssignment.type",
  "type.id",
  "type.name",
  "date",
  "versionNumber",
  "isSettled",
  "visibleToUser",
  "hasAttachments",
] as const;
type DueDateField = (typeof DUE_DATES_FIELDS)[number];
type DueDateSearch = NestedPick<DueDateNoCarDependency, DueDateField>;

type DueDatesProps = {
  isSettled?: boolean;
  isExpiringSoon?: boolean;
  isExpired?: boolean;
  onUpdate?: () => void;
};

const DueDates: FC<DueDatesProps> = ({ isSettled, isExpiringSoon, isExpired, onUpdate }) => {
  const i18n = useI18n();
  const filters = useListFilters({ isSearchable: true, prefix: "ds" });
  const debouncedValue = useDebounce(filters.state.q);
  const aborter = useRef(new AbortController());
  const dispatch = useContext(NotificationDispatch);

  const [showNewDueDate, setShowNewDueDate] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<DueDateSearch>();
  const [itemToEdit, setItemToEdit] = useState<DueDateSearch>();
  const [showDueDateTypes, setShowDueDateTypes] = useState(false);
  const [selectedRows, setSelectedRows] = useState<{ id: string; carId: string; date: string }[]>([]);
  const [showRenewDueDates, setShowRenewDueDates] = useState(false);
  const [showSetSettledDueDates, setShowSetSettledDueDates] = useState(false);
  const [dueDateTypeIdToEdit, setDueDateTypeIdToEdit] = useState<string>();

  const userSettings = useUserSettings();

  const dueDatesApi = useDueDatesApi();
  const [itemToShowVersions, setItemToShowVersions] = useState<DueDateSearch>();
  const { id: carId } = useParams<{ id: string }>();

  useEffect(() => {
    filters.setFilters("dueDate.isSettled", {
      operator: undefined,
      values: isSettled || isSettled === false ? [!!isSettled] : [],
    });
  }, [isSettled]);

  const query: PaginationQueryWithFields<DueDateNoCarDependency> = {
    ...filters.state,
    q: debouncedValue,
    fields: DUE_DATES_FIELDS,
  };

  const result = dueDatesApi.useAdminDueDates(query, aborter.current.signal, carId, isExpiringSoon, isExpired);
  const [data] = useDataInitializer(result.data, result.error);

  const unSettled = data?.items && data.items.length > 0 ? data?.items.filter((i) => !i.isSettled) : [];

  const filterOptions = {
    get: dueDatesApi.getFieldFilters,
    update: filters.setFilters,
    getSelected: filters.getFilters,
  };

  const orderOptions = {
    by: filters.state.orderBy,
    desc: filters.state.desc,
    setBy: filters.setOrder,
  };

  const toggleAll = () => {
    if (!unSettled || unSettled.length === 0) {
      setSelectedRows([]);
      return;
    }
    const selection: DueDateSearch[] = unSettled.reduce((rv: DueDateSearch[], i) => {
      if (selectedRows.length !== unSettled.length) {
        return [...rv, i];
      }
      return rv;
    }, []);

    const newData = selection.map((d) => ({
      id: d.id,
      carId: d.car?.id ?? d.car?.id ?? "",
      date: d.date,
    }));

    setSelectedRows(newData);
  };

  const onToggleRow = (item: DueDateSearch, checked: boolean) => {
    checked
      ? setSelectedRows((prev) => [...prev, { id: item.id, date: item.date, carId: item.car.id }])
      : setSelectedRows((prev) => [...prev.filter((x) => x.id !== item.id)]);
  };

  const baseUrl = carId ? `/admin/cars/${carId}/due_dates` : `/admin/due_dates`;

  const viewItems = [
    {
      label: i18n.t.dueDates.all,
      url: baseUrl,
    },
    {
      label: i18n.t.common.notSettled,
      url: `${baseUrl}/not_settled`,
    },
    {
      label: i18n.t.common.settled,
      url: `${baseUrl}/settled`,
    },
    {
      label: i18n.t.common.expiringSoon,
      url: `${baseUrl}/expiring_soon`,
    },
    {
      label: i18n.t.common.expired,
      url: `${baseUrl}/expired`,
    },
  ];

  const title =
    isSettled === true
      ? i18n.t.common.settled
      : isSettled === false
        ? i18n.t.common.notSettled
        : isExpiringSoon
          ? i18n.t.common.expiringSoon
          : isExpired
            ? i18n.t.common.expired
            : i18n.t.dueDates.all;

  const items = viewItems.filter((item) => item.label !== title);

  const views = { title, items };

  const getRowActions = (item: DueDateSearch): Action[] => {
    const actions: Action[] = [];

    if (userSettings.permissions.dueDates.update) {
      actions.push(getEditAction(i18n, () => setItemToEdit(item)));
    }

    actions.push(getHistoryAction(i18n, () => setItemToShowVersions(item)));

    if (userSettings.permissions.dueDates.delete) {
      actions.push(getDeleteAction(i18n, () => setItemToDelete(item), true));
    }

    return actions;
  };

  const content = (
    <>
      <Table<DueDateSearch>
        filters={filters}
        views={!carId ? views : undefined}
        searchOptions={{
          onChange: () => {
            aborter.current.abort();
            aborter.current = new AbortController();
          },
        }}
        buttons={{
          main: userSettings.permissions.dueDates.insert && (
            <Button buttonProps={{ onClick: () => setShowNewDueDate(true) }} glyph={SvgAdd} />
          ),
          secondary: userSettings.permissions.dueDateTypes.read && (
            <Button
              buttonProps={{
                onClick: () => setShowDueDateTypes(true),
              }}
            >
              {i18n.t.common.manageDueDateTypes}
            </Button>
          ),
        }}
        head={
          <>
            {!carId && (
              <>
                <th></th>
                <Table.Th
                  title={i18n.t.plateNumbers.singular}
                  fieldName="dueDate.car.plateNumber"
                  filters={filterOptions}
                  filter={TextColumnFilter}
                  order={orderOptions}
                />
                <Table.Th
                  title={i18n.t.common.carVin}
                  fieldName="dueDate.car.vin"
                  filters={filterOptions}
                  filter={TextColumnFilter}
                  order={orderOptions}
                />
              </>
            )}
            <Table.Th
              title={i18n.t.common.type}
              fieldName="dueDate.type.name"
              filters={filterOptions}
              filter={TextColumnFilter}
              order={orderOptions}
            />
            <Table.Th
              title={i18n.t.common.date}
              fieldName="dueDate.date"
              filters={filterOptions}
              filter={DateColumnFilter}
              order={orderOptions}
              formatter={(value) =>
                typeof value === "string" && value ? formatDateNumeric(value, i18n.language) : undefined
              }
            />
            <Table.Th
              title={i18n.t.common.currentDriver}
              fieldName="dueDate.driver.name"
              filters={filterOptions}
              filter={TextColumnFilter}
              order={orderOptions}
            />
            <Table.Th
              title={i18n.t.common.visibleToUser}
              fieldName="dueDate.visibleToUser.fullName"
              filters={filterOptions}
              filter={TextColumnFilter}
              order={orderOptions}
            />
            {!(isSettled || isSettled === false) && (
              <Table.Th
                title={i18n.t.common.isSettled}
                fieldName="dueDate.isSettled"
                filters={filterOptions}
                filter={BooleanColumnFilter}
              />
            )}
            <th></th>
          </>
        }
        onRowClick={(item, event) => {
          event.preventDefault();
          setItemToEdit(item);
        }}
        selectable={{
          onRowSelect: onToggleRow,
          isRowSelected: (item) => selectedRows.map((x) => x.id).includes(item.id),
          isRowSelectable: (item) => !item.isSettled,
          selectAll: {
            selected: !!unSettled.length && selectedRows.length === unSettled.length,
            toggle: toggleAll,
          },
        }}
        listActions={[
          {
            label: i18n.t.dueDates.renewSelected,
            onClick: () => {
              if (!selectedRows.length) {
                dispatch(showInformativeNotification(i18n.t.common.noDueDateSelected));
                return;
              }
              setShowRenewDueDates(true);
            },
            icon: SvgRefresh,
          },
          {
            label: i18n.t.dueDates.setSettledSelected,
            onClick: () => {
              if (!selectedRows.length) {
                dispatch(showInformativeNotification(i18n.t.common.noDueDateSelected));
                return;
              }
              setShowSetSettledDueDates(true);
            },
            icon: SvgCheckCircle,
          },
        ]}
        renderRow={(item) => (
          <>
            {!carId && (
              <>
                <td>{item.hasAttachments ? <Check icon={SvgAttach} /> : ""}</td>
                <td>
                  {isWithinLast48h(item.createdAt) && <NewItemIcon />}
                  {userSettings.permissions.cars.read ? (
                    <CustomLink to={`/admin/cars/${item.car.id}`}>
                      {item.car.currentPlateNumber || item.car.lastPlateNumber}
                    </CustomLink>
                  ) : (
                    <>{item.car.currentPlateNumber || item.car.lastPlateNumber}</>
                  )}
                </td>
                <td>
                  {userSettings.permissions.cars.read ? (
                    <CustomLink to={`/admin/cars/${item.car.id}`}>{item.car.vehicleIdentificationNumber}</CustomLink>
                  ) : (
                    item.car.vehicleIdentificationNumber
                  )}
                </td>
              </>
            )}
            <td>
              {!!carId && isWithinLast48h(item.createdAt) && <NewItemIcon />}
              <CustomLink
                to=""
                onClick={(e) => {
                  e.preventDefault();
                  setDueDateTypeIdToEdit(item.type.id);
                }}
              >
                {item.type.name}
              </CustomLink>
            </td>
            <td>{formatDateNumeric(item.date, i18n.language)}</td>
            <td>
              <CustomLink
                to={
                  item.car.currentAssignment?.carDriver
                    ? `/admin/users/${item.car.currentAssignment.carDriver?.driver?.id}`
                    : ""
                }
              >
                {item.car.currentAssignment?.carDriver?.driver?.fullName}
              </CustomLink>
            </td>
            <td> {item.visibleToUser?.fullName} </td>
            {!(isSettled || isSettled === false) && (
              <td>
                <Check
                  icon={item.isSettled ? SvgCheckCircle : SvgCancel}
                  color={item.isSettled ? "green" : "red"}
                  obscured={item.isSettled}
                />
              </td>
            )}
          </>
        )}
        getRowStyle={(item) => {
          if (isExpiringSoon !== undefined) return undefined;
          if (isItemExpired(item.date, item.isSettled)) {
            return { "--row-bg": "var(--red-20)", "--row-hover-bg": "var(--red-40)" } as React.CSSProperties;
          }
          if (isItemExpiringSoon(item.date, item.isSettled)) {
            return { "--row-bg": "var(--yellow-20)", "--row-hover-bg": "var(--yellow-40)" } as React.CSSProperties;
          }
          return undefined;
        }}
        rowActions={getRowActions}
        error={result.error}
        items={data?.items}
        total={data?.total}
      />

      {!!showNewDueDate && (
        <CreateDueDateDialog
          onClose={(success) => {
            setShowNewDueDate(false);
            success && onUpdate?.();
          }}
          carId={carId}
        />
      )}

      {!!itemToDelete && (
        <DeleteDueDateDialog
          dueDateId={itemToDelete.id}
          carId={itemToDelete.car.id}
          onClose={(success) => {
            setItemToDelete(undefined);
            success && onUpdate?.();
          }}
        />
      )}

      {!!itemToEdit && (
        <EditDueDate
          dueDateId={itemToEdit.id}
          onClose={(success) => {
            setItemToEdit(undefined);
            success && onUpdate?.();
          }}
          carId={carId}
        />
      )}

      {showDueDateTypes && <DueDateTypes onClose={() => setShowDueDateTypes(false)} />}

      {showRenewDueDates && (
        <RenewDueDatesDialog
          dueDates={selectedRows}
          onClose={(reset) => {
            setShowRenewDueDates(false);
            reset && setSelectedRows([]);
            onUpdate?.();
          }}
        />
      )}

      {showSetSettledDueDates && (
        <SetSettledDueDatesDialog
          dueDates={selectedRows}
          onClose={(success) => {
            setShowSetSettledDueDates(false);
            success && setSelectedRows([]);
            onUpdate?.();
          }}
        />
      )}

      {!!itemToShowVersions && (
        <DueDateHistroryDialog
          itemToShowVersionsId={itemToShowVersions.id}
          itemVersionToShowVersions={itemToShowVersions.versionNumber}
          onClose={() => setItemToShowVersions(undefined)}
        />
      )}

      {!!dueDateTypeIdToEdit && (
        <EditDueDateTypeDialog dueDateTypeId={dueDateTypeIdToEdit} onClose={() => setDueDateTypeIdToEdit(undefined)} />
      )}
    </>
  );

  return carId ? (
    content
  ) : (
    <>
      <Header title={i18n.t.dueDates.plural} />
      <Panel>
        {!isSettled && isSettled !== false && !isExpiringSoon && !isExpired && <DueDateStats query={query} />}
        {content}
      </Panel>
    </>
  );
};

export default DueDates;
